<template>
  <!-- Loading State -->
  <div v-if="loading" class="flex items-center justify-center min-h-screen">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>

  <!-- Error State -->
  <PageError v-else-if="error" :error="error" :errorCode="errorCode" />

  <div v-else-if="item" class="p-6 dark:text-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
          Back to Network Devices
        </button>
        Network Device: {{ item.name }}
        <div class="relative group">
          <button
            type="button"
            class="hover:bg-transparent bg-transparent ml-2"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
    </div>

    <!-- Field table -->
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 backdrop-blur-md border-b border-slate-700/50">
          <tr>
            <th class="py-2 px-4 w-1/3">Field</th>
            <th class="py-2 px-4 w-2/3">Value</th>
          </tr>
        </thead>
        <tbody v-for="group in groupedFields" :key="group.title">
          <tr class="bg-slate-100 dark:bg-slate-800/60">
            <td colspan="2" class="py-2 px-4 font-mono text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{{ group.title }}</td>
          </tr>
          <tr
            v-for="field in group.fields"
            :key="field.key"
            :id="'network-device-field-' + field.key"
            :class="['border-b dark:border-gray-700 hover:bg-gray-900/30 transition-colors']"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              {{ formatValue(field.key, item[field.key]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getNetworkDeviceById } from "@/services/networkDeviceService";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import { methodLabel, protocolLabel } from "@/utils/deviceMonitoring";
import PageError from "@/components/PageError.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

// FK/choice fields whose display name the serializer returns inline.
const NAME_FIELDS = {
  site: "site_name",
  probe: "probe_name",
  auth: "auth_name",
  snmp_metric_profile: "snmp_metric_profile_name",
  device_type: "device_type_label",
  tech_type: "tech_type_label",
};

export default {
  name: "NetworkDeviceDetails",
  components: {
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      item: null,
      loading: false,
      error: "",
      errorCode: "",
      networkDeviceDetailSteps: [
        {
          element: '#network-device-field-name',
          action: 'display',
          message: 'The name identifying this network device.'
        },
        {
          element: '#network-device-field-target',
          action: 'display',
          message: 'The target destination (IP or FQDN) being monitored.'
        },
        {
          element: '#network-device-field-device_type',
          action: 'display',
          message: 'The type of device (switch, router, firewall, etc.).'
        },
        {
          element: '#network-device-field-method',
          action: 'display',
          message: 'The metrics collection method configured for this device (SNMP, API, or Fabric).'
        },
        {
          element: '#network-device-field-reachability',
          action: 'display',
          message: 'Whether reachability monitoring is enabled for this device.'
        },
        {
          element: '#network-device-field-in_maintenance',
          action: 'display',
          message: 'Whether the device is in maintenance. Alerting is paused while enabled.'
        },
      ],
    };
  },
  computed: {
    displayFields() {
      return [
        { key: "name", label: "Device Name", type: "text", section: "Identity" },
        { key: "target", label: "Target Destination", type: "text", section: "Identity" },
        { key: "in_maintenance", label: "In Maintenance", type: "checkbox", section: "Identity" },
        { key: "device_type", label: "Device Type", type: "select", section: "Identity" },
        { key: "tech_type", label: "Tech Type", type: "text", section: "Identity", applies: (device) => !!device.tech_type },
        { key: "site", label: "Site", type: "select", section: "Anchor", applies: (device) => !!device.site },
        { key: "probe", label: "Probe", type: "select", section: "Anchor", applies: (device) => !!device.probe },
        { key: "method", label: "Monitoring Method", type: "text", section: "Monitoring", applies: (device) => !!device.method },
        { key: "auth", label: "SNMP Auth Profile", type: "select", section: "Monitoring", applies: (device) => device.method === "snmp" },
        { key: "snmp_metric_profile", label: "SNMP Metric Profile (walk-OID set)", type: "select", section: "Monitoring", applies: (device) => device.method === "snmp" },
        { key: "api_endpoint", label: "API Endpoint", type: "text", section: "Monitoring", applies: (device) => device.method === "api" },
        { key: "api_metrics", label: "API Metric Groups", type: "text", section: "Monitoring", applies: (device) => device.method === "api" },
        { key: "fabric_target", label: "Fabric Target", type: "text", section: "Monitoring", applies: (device) => device.method === "fabric" },
        { key: "reachability", label: "Reachability Monitoring", type: "checkbox", section: "Monitoring" },
        { key: "reachability_protocol", label: "Reachability Protocol", type: "text", section: "Monitoring", applies: (device) => device.reachability },
        { key: "reachability_port", label: "Reachability Port", type: "number", section: "Monitoring", applies: (device) => device.reachability && device.reachability_protocol === "tcp" },
        { key: "scrape_interval", label: "Scrape Interval", type: "number", staffOnly: true, section: "Metadata" },
        { key: "scrape_timeout", label: "Scrape Timeout", type: "number", staffOnly: true, section: "Metadata" },
        { key: "job_name", label: "Job Name", type: "text", staffOnly: true, section: "Metadata" },
        { key: "last_modified", label: "Last Modified", type: "text", section: "Metadata" },
        { key: "created_date", label: "Created Date", type: "text", section: "Metadata" },
      ];
    },
    visibleFields() {
      const device = this.item || {};
      return this.displayFields.filter(
        (field) =>
          (!field.staffOnly || useStore.isStaff()) &&
          (!field.applies || field.applies(device))
      );
    },
    groupedFields() {
      const order = ["Identity", "Anchor", "Monitoring", "Metadata"];
      return order
        .map((title) => ({ title, fields: this.visibleFields.filter((field) => field.section === title) }))
        .filter((group) => group.fields.length);
    },
  },
  async mounted() {
    const id = this.$route.params.id;
    try {
      this.loading = true;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to get network device details!";
        this.errorCode = "422!";
        return;
      }
      this.item = await getNetworkDeviceById(id, validation.client);
    } catch (error) {
      console.error("Error fetching network device:", error);
      const { code, message } = formatError(error);
      this.errorCode = code;
      this.error = message;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async startTutorial() {
      await this._start(this.networkDeviceDetailSteps);
    },
    goBack() {
      this.$router.push({
        name: "NetworkDevices",
        query: this.tenant.addSchemaParam(),
      });
    },
    formatValue(key, value) {
      if (value === null || value === undefined || value === "") return "N/A";
      if (key === "reachability") return value ? "Enabled" : "Disabled";
      if (typeof value === "boolean") return value ? "Yes" : "No";

      const nameField = NAME_FIELDS[key];
      if (nameField) return this.item?.[nameField] || value;

      if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
      if (key === "method") return methodLabel(value);
      if (key === "reachability_protocol") return protocolLabel(value);
      return value;
    },
  },
};
</script>
